/**
 * Turning a growth series into something a chart can draw (P2-TR4).
 *
 * Kept out of the chart component so it can be tested without rendering
 * anything, and so the one place weights become numbers is visible. Recharts
 * needs numbers to compute pixel positions; nothing derived from them is shown
 * as a weight, and every figure the partner reads comes from the server's
 * string (MONEY-1 applies to weights for the same reason it applies to money).
 */

import type { GrowthPoint, GrowthSeries } from './api.js';

export interface ChartPoint {
  /** Days since the first weighing — the x axis (P2-TR4). */
  dayOffset: number;
  date: string;
  /** Display geometry only. */
  actual: number;
  expected: number;
  /** The strings the tooltip and the table show. */
  actualKg: string;
  expectedKg: string;
  dailyGainGrams: number | null;
}

/**
 * The series as points, chronological, with the reference band alongside.
 *
 * Sorted by `dayOffset` rather than trusted to arrive in order: a weight
 * recorded late — a partner writing up last week's readings on Friday — would
 * otherwise draw a line that doubles back on itself, which reads as the animal
 * having lost and regained weight.
 */
export function toChartPoints(points: readonly GrowthPoint[]): ChartPoint[] {
  return [...points]
    .sort((a, b) => a.dayOffset - b.dayOffset)
    .map((point) => ({
      dayOffset: point.dayOffset,
      date: point.date,
      actual: Number(point.weightKg),
      expected: Number(point.expectedKg),
      actualKg: point.weightKg,
      expectedKg: point.expectedKg,
      dailyGainGrams: point.dailyGainGrams,
    }));
}

export interface GrowthVerdict {
  /** What to say about the animal in one line. */
  key: 'growth.onTrack' | 'growth.behind' | 'growth.tooFewReadings' | 'growth.neverWeighed';
  tone: 'good' | 'warn' | 'neutral';
  /** Rounded for display; the server computed it. */
  averageDailyGainGrams: number | null;
}

/**
 * What the profile says above the chart.
 *
 * "Behind the reference" is a prompt to look at the animal, not a verdict on
 * it — a goat can be below the band because it is ill, or because it was
 * bought thin and is catching up. So the wording stays neutral and the tone is
 * a warning rather than an error.
 */
export function growthVerdict(
  series: Pick<GrowthSeries, 'onTrack' | 'averageDailyGainGrams' | 'points'>,
): GrowthVerdict {
  if (series.points.length === 0) {
    return { key: 'growth.neverWeighed', tone: 'neutral', averageDailyGainGrams: null };
  }

  // One reading is a fact about a day, not a growth rate.
  if (series.onTrack === null || series.averageDailyGainGrams === null) {
    return { key: 'growth.tooFewReadings', tone: 'neutral', averageDailyGainGrams: null };
  }

  return {
    key: series.onTrack ? 'growth.onTrack' : 'growth.behind',
    tone: series.onTrack ? 'good' : 'warn',
    averageDailyGainGrams: series.averageDailyGainGrams,
  };
}
